import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import BigNumber from "bignumber.js";
import dayjs from "dayjs";
import { v4 } from "uuid";
import { findSalesAggregates, saveSale } from "../services/sales";
import { selectAirlineByTktNumber } from "./airlines";
import {
  downloadCsvFile,
  formatDate,
  formatDateTime,
  getUserFullName,
  isBetweenDateInterval,
  itinerarySegmentToString,
  segmentFlightDateTime,
  toDateOnlyString,
} from "../utils";
import { selectCanEditAllSales } from "./auth";
import {
  showErrorNotification,
  showInfoNotification,
  showSuccessNotification,
} from "./notifications";
import { selectCurrentSalesAgent } from "./salesAgents";
import {
  filtersToParams,
  loadSearchResults,
  resetFilters,
  selectAppliedFilters,
  selectNextSearchResultsCursor,
} from "./search";

const salesAdapter = createEntityAdapter({
  sortComparer: (a, b) =>
    `${b.saleDate}${b.savedAt}`.localeCompare(`${a.saleDate}${a.savedAt}`),
});

const AIR_SALE_TYPES = ["ES", "ER", "EV", "EX", "RC", "TS", "TR", "TV", "TX"];
const isAirSale = (sale) => AIR_SALE_TYPES.includes(sale.type);

const addIdToSegment = (segment) => ({ ...segment, id: v4() });

const processForStorage = (sale) =>
  isAirSale(sale) && Array.isArray(sale?.itinerary)
    ? {
        ...sale,
        itinerary: sale.itinerary.map(addIdToSegment),
      }
    : sale;

const SalesAggregates = (agg) => ({
  totalCommission: agg.totalCommission || 0,
  totalCount: agg.totalCount || 0,
  totalNumOfSegments: agg.totalNumOfSegments || 0,
  totalPenalty: agg.totalPenalty || "0.00",
  totalPrice: agg.totalPrice || "0.00",
  totalServiceFee: agg.totalServiceFee || "0.00",
  totalTax: agg.totalTax || "0.00",
});

const initialStateAggregates = SalesAggregates({});

const initialStateEdit = {
  id: null,
  isSaving: false,
};

const initialStateReport = {
  cachedAt: null,
  isDownloading: false,
};

const initialState = {
  ...salesAdapter.getInitialState(),
  ...initialStateAggregates,
  edit: initialStateEdit,
  report: initialStateReport,
};

export const calculateSaleFields = (sale) => {
  const negativeSaleTypes = ["CR", "ER", "EV", "HR", "TR", "TV"];
  const multiplier = negativeSaleTypes.includes(sale.type) ? "-1" : "1";
  const commission = new BigNumber(sale.commission || 0).multipliedBy(
    multiplier,
  );
  const numOfSegments = (sale.numOfSegments || 0) * multiplier;
  const penalty = new BigNumber(sale.penalty || 0);
  const price = new BigNumber(sale.price || 0).multipliedBy(multiplier);
  const serviceFee = new BigNumber(sale.serviceFee || 0).multipliedBy(
    multiplier,
  );
  const tax = new BigNumber(sale.tax || 0).multipliedBy(multiplier);
  const netTotal = price.plus(tax).minus(commission).plus(penalty);
  const total = price.plus(tax).plus(serviceFee).plus(penalty);
  return {
    commission,
    netTotal,
    numOfSegments,
    penalty,
    price,
    serviceFee,
    tax,
    total,
  };
};

const calculateAggregates = (aggregates, sale) =>
  (({ numOfSegments, penalty, price, serviceFee, tax }) => ({
    totalCount: (aggregates.totalCount || 0) + 1,
    totalNumOfSegments: aggregates.totalNumOfSegments + (numOfSegments || 0),
    totalPenalty: new BigNumber(aggregates.totalPenalty)
      .plus(penalty || 0)
      .toFixed(2),
    totalPrice: new BigNumber(aggregates.totalPrice)
      .plus(price || 0)
      .toFixed(2),
    totalServiceFee: new BigNumber(aggregates.totalServiceFee)
      .plus(serviceFee || 0)
      .toFixed(2),
    totalTax: new BigNumber(aggregates.totalTax).plus(tax || 0).toFixed(2),
  }))(calculateSaleFields(sale));

const calculateAggregatesFromData = (data) =>
  (data || []).reduce(calculateAggregates, initialStateAggregates);

const salesSlice = createSlice({
  name: "sales",
  initialState,
  reducers: {
    cancelEdit: (state) => ({ ...state, edit: initialStateEdit }),
    edit: (state, { payload }) => {
      state.edit.id = payload.id;
    },
    updateSale: salesAdapter.updateOne,
  },
  extraReducers: (builder) => {
    builder
      .addCase(cacheReport.fulfilled, (state, { payload }) => {
        state.report.cachedAt = payload.cachedAt;
      })
      .addCase(downloadReport.pending, (state) => {
        state.report.isDownloading = true;
      })
      .addCase(downloadReport.fulfilled, (state) => {
        state.report.isDownloading = false;
      })
      .addCase(downloadReport.rejected, (state) => {
        state.report.isDownloading = false;
      })
      .addCase(updateSalesAggregates.fulfilled, (state, { payload }) => ({
        ...state,
        ...SalesAggregates(payload),
      }))
      .addCase(loadSearchResults.fulfilled, (state, { payload, meta }) =>
        (meta?.arg ? salesAdapter.setMany : salesAdapter.setAll)(
          {
            ...state,
            ...(payload?.aggregates
              ? SalesAggregates(payload.aggregates)
              : calculateAggregatesFromData(payload?.data)),
            report: meta?.arg
              ? state.report
              : { ...state.report, cachedAt: null },
          },
          payload?.data.map(processForStorage) || [],
        ),
      )
      .addCase(resetFilters, (state) => initialState)
      .addCase(save.fulfilled, (state, { payload }) =>
        salesAdapter.setOne({ ...state, edit: initialStateEdit }, payload),
      )
      .addCase(save.pending, (state) => {
        state.edit.isSaving = true;
      })
      .addCase(save.rejected, (state) => {
        state.edit.isSaving = false;
      });
  },
});

export default salesSlice.reducer;

export const { cancelEdit, edit } = salesSlice.actions;

export const expandSale = ({ id, expanded }) =>
  salesSlice.actions.updateSale({ id, changes: { expanded } });

const isValidItinerary = (itinerary) =>
  (itinerary || []).every(
    ({ o, d }) => d && d.length === 3 && o && o.length === 3,
  );

const isEmpty = (value) =>
  value === null || value === undefined || value === "";

const isValidPnr = (value) => isEmpty(value) || /^[0-9A-Z]{6,8}$/u.test(value);

const isValidTkt = (value) => /^\d{13}$/.test(value);

const isValidBaseSale = (sale) => !!sale.paymentMethod && !!sale.saleDate;

const isValidAirSale = (sale) =>
  isValidBaseSale(sale) &&
  isValidItinerary(sale.itinerary) &&
  !!sale.pax &&
  isValidPnr(sale.pnr) &&
  isValidTkt(sale.tkt);

const isValidEmdSale = (sale) =>
  isValidAirSale(sale) && isValidTkt(sale.relTkt);

const isValidTktExchange = (sale) =>
  isValidAirSale(sale) && isValidTkt(sale.relTkt);

const isValidBookingConfirmationNumber = (value) =>
  isEmpty(value) || /^[0-9A-Z-]{6,50}$/u.test(value);

const isValidCheckInCheckOutDates = (ciDate, coDate) =>
  (isEmpty(ciDate) && isEmpty(coDate)) || dayjs(coDate).isAfter(dayjs(ciDate));

const isValidCruiseSale = (sale) =>
  isValidBaseSale(sale) &&
  !!sale.company &&
  !!sale.liner &&
  !!sale.pax &&
  isValidCheckInCheckOutDates(sale.ciDate, sale.coDate) &&
  isValidBookingConfirmationNumber(sale.pnr);

const isValidHotelSale = (sale) =>
  isValidBaseSale(sale) &&
  !!sale.hotel &&
  !!sale.loc &&
  !!sale.pax &&
  isValidCheckInCheckOutDates(sale.ciDate, sale.coDate) &&
  isValidBookingConfirmationNumber(sale.pnr);

const isValidOtherSale = (sale) =>
  isValidBaseSale(sale) &&
  !!sale.pax &&
  !!sale.serviceDate &&
  isValidBookingConfirmationNumber(sale.pnr);

const VALIDATORS_BY_SALE_TYPE = {
  ER: isValidEmdSale,
  ES: isValidEmdSale,
  CR: isValidCruiseSale,
  CS: isValidCruiseSale,
  HR: isValidHotelSale,
  HS: isValidHotelSale,
  TR: isValidAirSale,
  TS: isValidAirSale,
  TX: isValidTktExchange,
  OR: isValidOtherSale,
  OS: isValidOtherSale,
};

export const isValidSale = (sale) =>
  (VALIDATORS_BY_SALE_TYPE[sale?.type] || (() => false))(sale);

export const selectors = salesAdapter.getSelectors((state) => state.sales);

export const selectAirSaleEditOpen = (state) => !!state.sales.edit.id;

export const selectAirSaleIsSaving = (state) => state.sales.edit.isSaving;

export const selectAirSalesTotalCount = (state) => state.sales.totalCount;

export const selectEditedAirSaleId = (state) => state.sales.edit.id;

export const selectEditSaleAuthorizer = createSelector(
  selectCanEditAllSales,
  selectCurrentSalesAgent,
  (canEditAllSales, currentSalesAgent) => {
    const minSaleDate = dayjs(toDateOnlyString(dayjs())).subtract(
      process.env.REACT_APP_SALES_OLD_AFTER_DAYS,
      "days",
    );
    return (sale) => {
      const sameSalesAgent = sale.sa?.id === currentSalesAgent?.id;
      return (
        canEditAllSales ||
        (sameSalesAgent && !dayjs(sale.saleDate).isBefore(minSaleDate))
      );
    };
  },
);

const REPORT_CACHE_EXPIRES_IN = 30 * 60 * 1000;
export const selectIsReportCached = (state) =>
  state.sales.report.cachedAt &&
  new Date() - new Date(state.sales.report.cachedAt) <= REPORT_CACHE_EXPIRES_IN;

export const selectIsReportDownloading = (state) =>
  state.sales.report.isDownloading;

export const selectTotalNumOfSegments = (state) =>
  state.sales.totalNumOfSegments;

export const selectTotalPenalty = (state) => state.sales.totalPenalty;

export const selectTotalPrice = (state) => state.sales.totalPrice;

export const selectTotalServiceFee = (state) => state.sales.totalServiceFee;

export const selectTotalTax = (state) => state.sales.totalTax;

export const selectGrandTotal = createSelector(
  selectTotalPenalty,
  selectTotalPrice,
  selectTotalServiceFee,
  selectTotalTax,
  (penalty, price, serviceFee, tax) =>
    new BigNumber(price).plus(serviceFee).plus(tax).plus(penalty).toFixed(2),
);

const filterComparators = {
  airline: (airSale) => (filter) => airSale.airline === filter.value,
  customer: (sale) => (filter) => sale.customer?.id === filter.value,
  flightDates: (airSale) => (filter) =>
    !!airSale?.itinerary
      ?.map((s) => s.dD)
      ?.filter((d) => !!d)
      ?.some(isBetweenDateInterval(filter.value)),
  src: (sale) => (filter) => sale.src === filter.value,
  pax: (sale) => (filter) => sale.pax.startsWith(filter.value),
  pnr: (sale) => (filter) => sale.pnr === filter.value,
  sa: (sale) => (filter) => sale.sa?.id === filter.value,
  saleDate: (sale) => (filter) =>
    isBetweenDateInterval(filter.value)(sale.saleDate),
  sup: (sale) => (filter) => sale.sup?.id === filter.value,
  tkt: (airSale) => (filter) => airSale.tkt === filter.value,
  type: (sale) => (filter) => sale.type === filter.value,
};

export const selectDoSearchFiltersApplyToSale = createSelector(
  (state, sale) => sale,
  selectAppliedFilters,
  (sale, appliedFilters) =>
    ((pnrFilter) =>
      (pnrFilter ? [pnrFilter] : appliedFilters).every(
        (filter) =>
          filterComparators[filter.id](sale)(filter) ||
          (sale._prev && filterComparators[filter.id](sale._prev)(filter)),
      ))(appliedFilters.find(({ id }) => id === "pnr")),
);

const getAirSaleTktNumbers = (sale) => [
  ...(sale.tkt ? [sale.tkt] : []),
  ...(sale.relTkt ? [sale.relTkt] : []),
];

const tktArrayEquals = (arr1) => (arr2) =>
  Array.isArray(arr1) &&
  Array.isArray(arr2) &&
  arr1.length === arr2.length &&
  arr1.every((item, index) => item === arr2[index]);

const filterSalesRelatedToTkt = (tkts) => (sales) =>
  ((foundSales) =>
    ((foundTkts) =>
      tktArrayEquals(foundTkts)(tkts.sort())
        ? foundSales
        : filterSalesRelatedToTkt(foundTkts)(sales))(
      [...new Set(foundSales.flatMap(getAirSaleTktNumbers))].sort(),
    ))(
    sales.filter((sale) =>
      getAirSaleTktNumbers(sale).some((tkt) => tkts.includes(tkt)),
    ),
  );

const escape = (str) =>
  new RegExp(/[\n"]/g).test(str)
    ? `"${str.replace(/"/g, '""').replace(/\n/g, "\\n")}"`
    : str;

const handleDescription = (sale) =>
  sale?.descr
    ? escape(sale.descr)
    : Array.isArray(sale.itinerary)
      ? sale.itinerary
          .map(({ descr }, i) => (descr ? `${i + 1}.${descr}` : ""))
          .filter((s) => !!s)
          .join("; ")
      : "";

const salePropToString = (sale) => {
  const {
    commission,
    netTotal,
    numOfSegments,
    penalty,
    price,
    serviceFee,
    tax,
    total,
  } = calculateSaleFields(sale);
  return (prop) => {
    const value = sale[prop];
    switch (prop) {
      case "ciDate":
        return value ? formatDate(value) : "";
      case "coDate":
        return value ? formatDate(value) : "";
      case "commission":
        return commission.toFixed(2);
      case "customer":
        return value?.name || "";
      case "descr":
        return handleDescription(sale);
      case "itinerary":
        return (value || []).map(itinerarySegmentToString).join(" ");
      case "itineraryDates":
        return (sale?.itinerary || []).map(segmentFlightDateTime).join(" ");
      case "numOfSegments":
        return `${numOfSegments}`;
      case "penalty":
        return penalty.toFixed(2);
      case "price":
        return price.toFixed(2);
      case "sa":
        return value?.name || "";
      case "saleDate":
        return value ? formatDate(value) : "";
      case "savedAt":
        return value ? formatDateTime(value) : "";
      case "savedBy":
        return value ? getUserFullName(value) : "";
      case "serviceDate":
        return value ? formatDate(value) : "";
      case "serviceFee":
        return serviceFee.toFixed(2);
      case "sup":
        return value?.name || "";
      case "tax":
        return tax.toFixed(2);
      case "netTotal":
        return netTotal.toFixed(2);
      case "total":
        return total.toFixed(2);
      default:
        return `${value === undefined || value === null ? "" : value}`;
    }
  };
};

const csvColumns = {
  saleDate: "Sale Date",
  type: "Type",
  pax: "Passenger",
  airline: "Airline",
  tkt: "Ticket",
  relTkt: "Related Ticket",
  itinerary: "Itinerary",
  itineraryDates: "Itinerary Dates",
  numOfSegments: "Segments",
  src: "Source",
  pnr: "PNR/Confirmation #",
  serviceDate: "Service Date",
  loc: "Location",
  hotel: "Hotel Name",
  ciDate: "Check-in",
  coDate: "Check-out",
  company: "Cruise Company",
  liner: "Cruise Liner",
  room: "Room",
  meal: "Meal",
  descr: "Description",
  sa: "Sales Agent",
  customer: "Customer",
  sup: "Supplier",
  savedAt: "Saved At",
  savedBy: "Saved By",
  paymentMethod: "Payment Method",
  price: "Price",
  tax: "Tax",
  commission: "Commission",
  serviceFee: "Service Fee",
  penalty: "Penalty",
  netTotal: "Net Total",
  total: "Total",
};
const csvColumnsProps = Object.keys(csvColumns);

const saleToCsvTransformer = (sale) => {
  const propMapper = salePropToString(sale);
  return csvColumnsProps.map((prop) => {
    const value = propMapper(prop);
    return value.includes(",") ? `"${value}"` : `${value}`;
  });
};

const cacheReport = createAsyncThunk(
  "sales/cacheReport",
  async (_, { dispatch, getState }) => {
    let __next = selectNextSearchResultsCursor(getState());
    do {
      const { data, meta } = await dispatch(loadSearchResults(__next)).unwrap();
      if (!selectors.selectTotal(getState()) && !data.length) {
        return { cachedAt: null };
      }
      __next = meta.__next;
    } while (__next);
    return { cachedAt: new Date().toISOString() };
  },
);

export const downloadReport = createAsyncThunk(
  "sales/downloadReport",
  async (_, { dispatch, getState }) => {
    const isReportCached = selectIsReportCached(getState());
    if (!isReportCached) {
      const { cachedAt } = await dispatch(cacheReport()).unwrap();
      if (!cachedAt) {
        dispatch(showInfoNotification("Report has no results to download"));
        return;
      }
    }
    const salesInReport = selectors.selectAll(getState());
    downloadCsvFile({
      data: salesInReport,
      fileName: "sales_report.csv",
      headers: Object.values(csvColumns),
      transformer: saleToCsvTransformer,
    });
    dispatch(showSuccessNotification("Report has been downloaded"));
  },
);

const updateSalesAggregates = createAsyncThunk(
  "sales/updateAggregates",
  (savedSale, { dispatch, getState }) => {
    const { tkt, ...params } = filtersToParams(
      selectAppliedFilters(getState()),
    );
    if (tkt) {
      const sales = Object.values({
        ...selectors.selectEntities(getState()),
        [savedSale.id]: savedSale,
      });
      return calculateAggregatesFromData(filterSalesRelatedToTkt([tkt])(sales));
    } else if (selectDoSearchFiltersApplyToSale(getState(), savedSale)) {
      return findSalesAggregates(params).catch((err) => {
        dispatch(
          showErrorNotification(
            "Fetching sales aggregates failed. Please try again later.",
          ),
        );
        throw err;
      });
    }
    return SalesAggregates(getState().sales);
  },
);

const assoc = (obj) => ({ id: obj.id });

const BaseSalePayload = (sale) => ({
  id: sale.id,
  paymentMethod: sale?.paymentMethod,
  saleDate: sale?.saleDate,
  type: sale?.type,
  ...(sale?.commission && sale?.commission !== "0.00"
    ? { commission: sale.commission }
    : {}),
  ...(sale?.customer ? { customer: assoc(sale.customer) } : {}),
  ...(sale?.descr ? { descr: sale.descr } : {}),
  ...(sale?.penalty && sale?.penalty !== "0.00"
    ? { penalty: sale.penalty }
    : {}),
  ...(sale?.price && sale?.price !== "0.00" ? { price: sale.price } : {}),
  ...(sale?.sa ? { sa: assoc(sale.sa) } : {}),
  ...(sale?.serviceFee && sale?.serviceFee !== "0.00"
    ? { serviceFee: sale.serviceFee }
    : {}),
  ...(sale?.sup ? { sup: assoc(sale.sup) } : {}),
  ...(sale?.tax && sale?.tax !== "0.00" ? { tax: sale.tax } : {}),
});

export const buildSegment = (segment) => ({
  ...(segment?.c ? { c: segment.c } : {}),
  d: segment?.d || "",
  ...(segment?.descr ? { descr: segment.descr } : {}),
  ...(segment?.dD ? { dD: segment.dD } : {}),
  ...(segment?.dT ? { dT: segment.dT } : {}),
  o: segment?.o || "",
});

const AirSalePayload = (sale) => ({
  airline: sale?.airline,
  pax: sale?.pax,
  tkt: sale?.tkt,
  ...(sale?.itinerary?.length > 0
    ? { itinerary: sale.itinerary.map(buildSegment) }
    : {}),
  ...(sale?.pnr ? { pnr: sale.pnr } : {}),
  ...(sale?.relTkt ? { relTkt: sale.relTkt } : {}),
});

const CruiseSalePayload = (sale) => ({
  company: sale?.company,
  liner: sale?.liner,
  pax: sale?.pax,
  ...(sale?.ciDate ? { ciDate: sale.ciDate } : {}),
  ...(sale?.coDate ? { coDate: sale.coDate } : {}),
  ...(sale?.meal ? { meal: sale.meal } : {}),
  ...(sale?.pnr ? { pnr: sale.pnr } : {}),
  ...(sale?.room ? { room: sale.room } : {}),
});

const HotelSalePayload = (sale) => ({
  hotel: sale?.hotel,
  loc: sale?.loc,
  pax: sale?.pax,
  ...(sale?.ciDate ? { ciDate: sale.ciDate } : {}),
  ...(sale?.coDate ? { coDate: sale.coDate } : {}),
  ...(sale?.meal ? { meal: sale.meal } : {}),
  ...(sale?.pnr ? { pnr: sale.pnr } : {}),
  ...(sale?.room ? { room: sale.room } : {}),
});

const OtherSalePayload = (sale) => ({
  pax: sale?.pax,
  serviceDate: sale?.serviceDate,
  ...(sale?.loc ? { loc: sale.loc } : {}),
  ...(sale?.pnr ? { pnr: sale.pnr } : {}),
});

const PAYLOADS_BY_SALE_TYPE = {
  ER: AirSalePayload,
  ES: AirSalePayload,
  CR: CruiseSalePayload,
  CS: CruiseSalePayload,
  HR: HotelSalePayload,
  HS: HotelSalePayload,
  TR: AirSalePayload,
  TS: AirSalePayload,
  TX: AirSalePayload,
  OR: OtherSalePayload,
  OS: OtherSalePayload,
};

const SalePayload = (sale) => ({
  ...BaseSalePayload(sale),
  ...(PAYLOADS_BY_SALE_TYPE[sale?.type] || (() => ({})))(sale),
});

export const save = createAsyncThunk(
  "sales/save",
  (sale, { dispatch, getState }) => {
    const id = selectEditedAirSaleId(getState());
    return saveSale(
      SalePayload({
        ...sale,
        ...(isAirSale(sale)
          ? {
              airline: selectAirlineByTktNumber(
                getState(),
                sale.tkt.slice(0, -10),
              )?.iata,
            }
          : {}),
        id,
      }),
    )
      .then((savedSale) => {
        dispatch(showSuccessNotification("Sale saved successfully"));
        dispatch(updateSalesAggregates(savedSale));
        return savedSale;
      })
      .catch((err) => {
        const message =
          err.status === 400
            ? "You provided incomplete or invalid sale data"
            : err.message;
        dispatch(showErrorNotification(message));
        throw err;
      });
  },
);
